import { useId, useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronRightIcon } from 'lucide-react';

import { cn } from '@/lib/utils';
import { CopyButton } from '@/shared/components/CopyButton';
import { byteLength, formatByteSize } from '../executionHistory';

export interface ToolBlockProps {
  /** The tool's name, or "Tool result" / "Tool error" for what came back. */
  title: string;
  /** One line beside the title, when the transcript recorded one. */
  summary?: string | null;
  /** What the body shows, and exactly what the copy control copies. */
  content: string;
  /** `error` keeps today's destructive treatment so a failure reads as one at a glance. */
  tone?: 'default' | 'error';
  /** Open on arrival. Errors are; everything else waits to be asked (spec 096 FR-007). */
  defaultOpen?: boolean;
  /**
   * How much is inside, in place of the byte size — an edit's "+3 −1" (spec 157
   * FR-002). Bytes say nothing about a change; lines do.
   */
  meta?: string;
  /**
   * A body of the caller's own in place of the raw `content` — an edit read as
   * a diff. `content` is still what the copy control copies.
   */
  children?: ReactNode;
}

/**
 * One tool call or tool result, folded to a line until the reader wants it.
 *
 * A run is mostly tool traffic, and every block used to arrive fully expanded as
 * a 16 rem box scrolling inside a scrolling drawer — a long run was a column of
 * boxes with the two sentences that mattered somewhere between them. Folding
 * turns the run back into a list a person can scan: the header says what the
 * tool was, what it did in a few words, and how much it produced, which is
 * enough to decide whether to open it (FR-006).
 *
 * The header is a real button with `aria-expanded`, not a hover target and not a
 * `<details>`: it is in the tab order, Enter and Space toggle it, and assistive
 * technology announces its state. Its accessible name is its visible text — tool,
 * summary, size — with nothing overriding it, so the size hint reaches a screen
 * reader as well as an eye. The body is `hidden` rather than unmounted so a
 * search on the page still finds what is inside, and so the copy control has
 * something to copy the moment it appears.
 *
 * Not the shared `Collapsible`: that primitive is what folds whole runs one level
 * up, and keeping the two levels distinct in the DOM is worth more than reusing
 * a trigger that would then need its size hint and copy control bolted on.
 */
export function ToolBlock({
  title,
  summary,
  content,
  tone = 'default',
  defaultOpen = false,
  meta,
  children,
}: ToolBlockProps) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const isError = tone === 'error';

  return (
    <div
      className={cn(
        'overflow-hidden rounded-md border',
        isError ? 'border-destructive/30' : 'border-border',
      )}
    >
      <button
        type="button"
        aria-expanded={open}
        aria-controls={bodyId}
        onClick={() => {
          setOpen((current) => !current);
        }}
        className={cn(
          'flex min-h-9 w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs pointer-coarse:min-h-11',
          isError
            ? 'bg-destructive/10 text-destructive'
            : 'bg-muted/40 text-muted-foreground',
          'focus-visible:ring-ring focus-visible:ring-3 focus-visible:outline-none focus-visible:ring-inset',
        )}
      >
        <ChevronRightIcon
          aria-hidden="true"
          strokeWidth={1.5}
          className={cn(
            'size-3.5 shrink-0 transition-transform duration-[var(--motion-duration-fast)]',
            open && 'rotate-90',
          )}
        />
        <span
          className={cn(
            'shrink-0 font-medium',
            isError ? 'text-destructive' : 'text-foreground',
          )}
        >
          {title}
        </span>
        {summary ? <span className="min-w-0 truncate">{summary}</span> : null}
        {/* How much is inside, before the reader opens it. Bytes, not lines: a
            one-line JSON blob and a thousand-line log are both "one entry". */}
        <span className="ml-auto shrink-0 tabular-nums">
          {meta ?? formatByteSize(byteLength(content))}
        </span>
      </button>
      <div id={bodyId} hidden={!open} className="relative">
        {children ?? (
          <pre
            className={cn(
              'scrollbar-slim max-h-96 overflow-auto p-3 pr-10 font-mono text-xs whitespace-pre-wrap [overflow-wrap:anywhere]',
              isError
                ? 'bg-destructive/10 text-destructive'
                : 'text-foreground',
            )}
          >
            {content}
          </pre>
        )}
        <CopyButton
          text={content}
          label="Copy"
          className="text-muted-foreground absolute top-1.5 right-1.5"
        />
      </div>
    </div>
  );
}
